import { useState, useEffect, type ReactNode } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import {
  useAdminDocuments,
  useCreateDocument,
  useDeleteDocument,
  useRestoreDocument,
} from "@/lib/queries"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Checkbox } from "@/components/ui/checkbox"
import { Plus, Pencil, Archive, Search, ArchiveRestore, AlertTriangle, Copy, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react"
import { ApiError } from "@/lib/api"
import type { AdminDocument } from "@/lib/types"
import { useCurrentAdmin } from "@/lib/queries"
import { hasPermission, PERMISSIONS } from "@/lib/permissions"
import { DOCUMENT_TYPE_LABELS } from "@/lib/documentTypes"

type SortKey = "title" | "document_type" | "status" | "compliance_tags" | "published_at"

// ponytail: client-side sort; move to server params if lists get paginated
function sortValue(doc: AdminDocument, key: SortKey): string | number {
  switch (key) {
    case "status":
      // Published > Published Draft > Draft > No Version
      return doc.has_published ? (doc.has_draft ? 2 : 3) : doc.has_draft ? 1 : 0
    case "compliance_tags":
      return doc.compliance_tags?.join(", ") ?? ""
    case "published_at":
      return doc.published_at ?? ""
    case "document_type":
      return DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type
    case "title":
      return doc.title
  }
}

const DOCUMENT_TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "clinical_review", label: "Clinical Reviews" },
  { value: "workflow_template", label: "Workflows" },
  { value: "supplemental", label: "Supplemental" },
  { value: "static_page", label: "Static Pages" },
  { value: "archived", label: "Archived" },
]

export function DocumentsListPage() {
  const navigate = useNavigate()
  const { data: currentAdmin } = useCurrentAdmin()
  const canEditDocs = hasPermission(currentAdmin, PERMISSIONS.EDIT_DOCS)
  const [filterType, setFilterType] = useState<string>("all")
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newDocTitle, setNewDocTitle] = useState("")
  const [newDocType, setNewDocType] = useState<string>("clinical_review")
  const [fromTemplate, setFromTemplate] = useState(true)
  const [duplicateFrom, setDuplicateFrom] = useState<string>("")

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null)

  const toggleSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s?.key === key && s.dir === 1 ? -1 : 1 }))

  const [archiveTarget, setArchiveTarget] = useState<{
    id: number
    title: string
    organizations?: string[]
  } | null>(null)

  const { data: documents = [], isLoading } = useAdminDocuments(
    filterType === "all" || filterType === "archived" ? undefined : filterType,
    filterType === "archived" ? "archived" : undefined,
    searchQuery || undefined
  )
  const createDocument = useCreateDocument()
  const deleteDocument = useDeleteDocument()
  const restoreDocument = useRestoreDocument()

  const allDocuments = useAdminDocuments()

  const handleCreateDocument = async () => {
    if (!newDocTitle.trim()) return

    try {
      const payload: {
        title: string
        document_type: string
        from_template?: string
        duplicate_from?: number
      } = {
        title: newDocTitle,
        document_type: newDocType,
      }

      if (fromTemplate) {
        payload.from_template = newDocType
      } else if (duplicateFrom) {
        payload.duplicate_from = parseInt(duplicateFrom)
      }

      const newDoc = await createDocument.mutateAsync(payload)
      setCreateDialogOpen(false)
      setNewDocTitle("")
      setNewDocType("clinical_review")
      setFromTemplate(true)
      setDuplicateFrom("")
      navigate({ to: "/admin/documents/$id", params: { id: String(newDoc.id) } })
    } catch (error) {
      console.error("Failed to create document:", error)
    }
  }

  const handleArchiveDocument = async (id: number, title: string) => {
    try {
      await deleteDocument.mutateAsync({ id })
      setArchiveTarget(null)
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const data = error.data as { organizations?: string[] }
        setArchiveTarget({ id, title, organizations: data.organizations })
      } else {
        console.error("Failed to archive document:", error)
      }
    }
  }

  const handleForceArchive = async () => {
    if (!archiveTarget) return
    try {
      await deleteDocument.mutateAsync({ id: archiveTarget.id, force: true })
      setArchiveTarget(null)
    } catch (error) {
      console.error("Failed to archive document:", error)
    }
  }

  const handleRestoreDocument = async (id: number) => {
    try {
      await restoreDocument.mutateAsync(id)
    } catch (error) {
      console.error("Failed to restore document:", error)
    }
  }

  const handleDuplicateDocument = async (doc: { id: number; title: string; document_type: string }) => {
    try {
      const newDoc = await createDocument.mutateAsync({
        title: `Copy of ${doc.title}`,
        document_type: doc.document_type,
        duplicate_from: doc.id,
      })
      navigate({ to: "/admin/documents/$id", params: { id: String(newDoc.id) } })
    } catch (error) {
      console.error("Failed to duplicate document:", error)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—"
    return new Date(dateString).toLocaleDateString("en-CA", { timeZone: "UTC" })
  }

  const duplicatableDocuments =
    allDocuments.data?.filter((d) => d.document_type === newDocType) || []

  const sortedDocuments = sort
    ? [...documents].sort((a, b) => {
        const av = sortValue(a, sort.key)
        const bv = sortValue(b, sort.key)
        const cmp =
          typeof av === "number"
            ? av - (bv as number)
            : String(av).localeCompare(String(bv))
        return cmp * sort.dir
      })
    : documents

  const SortableHead = ({ sortKey, children }: { sortKey: SortKey; children: ReactNode }) => (
    <TableHead>
      <button
        type="button"
        className="flex items-center gap-1 hover:text-foreground"
        onClick={() => toggleSort(sortKey)}
      >
        {children}
        {sort?.key === sortKey ? (
          sort.dir === 1 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </TableHead>
  )

  return (
    <div className="container mx-auto py-8 overflow-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground mt-1">
            Manage clinical reviews, workflows, and supplemental content
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          {canEditDocs && (
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Document
              </Button>
            </DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Document</DialogTitle>
              <DialogDescription>
                Create a new document from a template or duplicate an existing
                one.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newDocTitle}
                  onChange={(e) => setNewDocTitle(e.target.value)}
                  placeholder="Enter document title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Document Type</Label>
                <Select value={newDocType} onValueChange={setNewDocType}>
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clinical_review">
                      Clinical Review
                    </SelectItem>
                    <SelectItem value="workflow_template">Workflow</SelectItem>
                    <SelectItem value="supplemental">Supplemental</SelectItem>
                    <SelectItem value="static_page">Static Page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="from-template"
                  checked={fromTemplate}
                  onCheckedChange={(checked: boolean | "indeterminate") => {
                    setFromTemplate(checked === true)
                    if (checked) setDuplicateFrom("")
                  }}
                />
                <Label htmlFor="from-template" className="font-normal">
                  Create from template
                </Label>
              </div>
              {!fromTemplate && (
                <div className="space-y-2">
                  <Label htmlFor="duplicate">Duplicate from (optional)</Label>
                  <Select
                    value={duplicateFrom}
                    onValueChange={setDuplicateFrom}
                  >
                    <SelectTrigger id="duplicate">
                      <SelectValue placeholder="Select document to duplicate" />
                    </SelectTrigger>
                    <SelectContent>
                      {duplicatableDocuments.map((doc) => (
                        <SelectItem key={doc.id} value={doc.id.toString()}>
                          {doc.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateDocument}
                disabled={!newDocTitle.trim() || createDocument.isPending}
              >
                {createDocument.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <Tabs value={filterType} onValueChange={setFilterType}>
          <TabsList>
            {DOCUMENT_TYPE_FILTERS.map((filter) => (
              <TabsTrigger key={filter.value} value={filter.value}>
                {filter.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative ml-auto w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">
          Loading documents...
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No documents found. Create your first document to get started.
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead sortKey="title">Title</SortableHead>
                <SortableHead sortKey="document_type">Type</SortableHead>
                <SortableHead sortKey="status">Status</SortableHead>
                {filterType !== "clinical_review" && filterType !== "static_page" && (
                  <SortableHead sortKey="compliance_tags">Compliance Tags</SortableHead>
                )}
                <SortableHead sortKey="published_at">Published Date</SortableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDocuments.map((doc) => (
                <TableRow key={doc.id} className="cursor-pointer">
                  <TableCell>
                    <Link
                      to="/admin/documents/$id"
                      params={{ id: doc.id.toString() }}
                      className="hover:underline font-medium"
                    >
                      {doc.title}
                    </Link>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 text-xs">
                      {doc.has_published && (
                        <span className="text-lime-600 font-medium">Published</span>
                      )}
                      {doc.has_draft && (
                        <span className="text-orange-500 font-medium">Draft</span>
                      )}
                      {!doc.has_published && !doc.has_draft && (
                        <span className="text-muted-foreground">No Version</span>
                      )}
                    </div>
                  </TableCell>
                  {filterType !== "clinical_review" && filterType !== "static_page" && (
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {doc.compliance_tags?.join(", ")}
                      </span>
                    </TableCell>
                  )}
                  <TableCell>{formatDate(doc.published_at)}</TableCell>
                  <TableCell className="text-right">
                    {filterType === "archived" ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => e.stopPropagation()}
                            title="Unarchive"
                          >
                            <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Unarchive Document</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to restore "{doc.title}"? It
                              will be moved back to the active documents list.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRestoreDocument(doc.id)}
                            >
                              Unarchive
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate({ to: "/admin/documents/$id", params: { id: doc.id.toString() } })
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={createDocument.isPending}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDuplicateDocument(doc)
                          }}
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            setArchiveTarget({ id: doc.id, title: doc.title })
                          }}
                        >
                          <Archive className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog
        open={archiveTarget !== null}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archiveTarget?.organizations
                ? "Document Has Active Assignments"
                : "Archive Document"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {archiveTarget?.organizations ? (
                  <>
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 mb-3 dark:border-amber-900 dark:bg-amber-950">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <span className="text-amber-800 dark:text-amber-200">
                        &quot;{archiveTarget.title}&quot; is currently assigned to
                        the following customers. Archiving will remove all
                        assignments.
                      </span>
                    </div>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      {archiveTarget.organizations.map((org) => (
                        <li key={org}>{org}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <span>
                    Are you sure you want to archive &quot;{archiveTarget?.title}&quot;?
                    It will be moved to the Archived tab.
                  </span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {archiveTarget?.organizations ? (
              <AlertDialogAction
                onClick={handleForceArchive}
                disabled={deleteDocument.isPending}
              >
                {deleteDocument.isPending ? "Archiving..." : "Unassign & Archive"}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={() => archiveTarget && handleArchiveDocument(archiveTarget.id, archiveTarget.title)}
                disabled={deleteDocument.isPending}
              >
                {deleteDocument.isPending ? "Archiving..." : "Archive"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
